// ── Skeletal itinerary day layout ───────────────────────────────────────────
// The skeletal grid stores cells as grid[`${day}_${slotKey}`]. Which slots a
// day has, and in what order, lives in `dayOrder[day]` (an array of slot keys)
// on requests/{id}/itineraryMeta/skeletal. Days without an entry use the
// default order, so skeletals saved before dayOrder existed read unchanged.
//
// Staff can move a day's lunch earlier/later, and add or remove activity
// slots (extra ones are keyed `act_<id>`).

export const DEFAULT_SLOT_KEYS = [
  'arrival', 'breakfast', 'morning1', 'lunch', 'afternoon1', 'afternoon2', 'evening1', 'evmeal', 'departure',
];

// Retired slots that can still hold data on older skeletals.
const LEGACY_AFTER = { morning2: 'morning1', evening2: 'evening1' };

export const SLOT_LABELS = {
  arrival: 'Arrival', breakfast: 'Breakfast', morning1: 'Morning', morning2: 'Morning',
  lunch: 'Lunch', afternoon1: 'Afternoon', afternoon2: 'Afternoon',
  evening1: 'Evening', evening2: 'Evening', evmeal: 'Evening Meal', departure: 'Departure',
};

// Lunch can't move past these.
const LUNCH_BOUNDS = new Set(['arrival', 'breakfast', 'evmeal', 'departure']);

/** 'meal' | 'flight' | 'slot' — what a slot holds by default. */
export function slotType(key) {
  if (key === 'breakfast' || key === 'lunch' || key === 'evmeal') return 'meal';
  if (key === 'arrival' || key === 'departure') return 'flight';
  return 'slot';
}

export function isExtraSlot(key) {
  return String(key).startsWith('act_');
}

/** The i18n/label key for a slot — extra activity slots share 'activity'. */
export function slotLabelKey(key) {
  return isExtraSlot(key) ? 'activity' : key;
}

export function slotLabel(key) {
  return isExtraSlot(key) ? 'Activity' : (SLOT_LABELS[key] || key);
}

/** A day's slot keys in order. */
export function daySlotKeys(dayOrder, grid, day) {
  const saved = dayOrder?.[day];
  if (Array.isArray(saved) && saved.length) return saved;
  const keys = [...DEFAULT_SLOT_KEYS];
  Object.entries(LEGACY_AFTER).forEach(([legacy, after]) => {
    if (grid?.[`${day}_${legacy}`]) keys.splice(keys.indexOf(after) + 1, 0, legacy);
  });
  return keys;
}

/** Move lunch one slot earlier (dir -1) or later (dir +1); unchanged when it can't. */
export function moveLunch(keys, dir) {
  const i = keys.indexOf('lunch');
  const j = i + dir;
  if (i < 0 || j < 0 || j >= keys.length || LUNCH_BOUNDS.has(keys[j])) return keys;
  const next = [...keys];
  [next[i], next[j]] = [next[j], next[i]];
  return next;
}

export function canMoveLunch(keys, dir) {
  return moveLunch(keys, dir) !== keys;
}

/** Add an activity slot before the evening meal (or at the end of the day). */
export function addActivitySlot(keys, newKey) {
  const at = ['evmeal', 'departure'].map((k) => keys.indexOf(k)).find((i) => i >= 0);
  const next = [...keys];
  next.splice(at ?? next.length, 0, newKey);
  return next;
}

export function removeSlot(keys, key) {
  return keys.filter((k) => k !== key);
}

export function newActivitySlotKey() {
  return `act_${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * The filled slots of a day, in order, for read-only views:
 * [{ key, cell }] — skipping hidden and empty ones.
 */
export function dayEntries({ grid = {}, hiddenCells = {}, dayOrder = {} }, day) {
  return daySlotKeys(dayOrder, grid, day)
    .map((key) => ({ key, cell: grid[`${day}_${key}`] }))
    .filter(({ key, cell }) => cell?.label && !hiddenCells[`${day}_${key}`]);
}
